// GAVION recruiting: in-memory RecruitingClient for the FakeRest demo and the tests. Same rules and codes as the RPCs
// of 91_gavion_recruiting.sql (admin check, slug, transitions, delete rule, purge_after).
import {
  EMPTY_AI_REVIEW,
  REVIEW_REQUEST_COOLDOWN_MS,
  reviewSummary,
  type AiReview,
} from "../aiReview";
import {
  APPLICATION_STATUSES,
  EMPLOYMENT_TYPES,
  REMOTE_MODES,
  SLUG_MAX,
  SLUG_PATTERN,
  freeSlug,
  initialPurgeAfter,
  purgeAfterFor,
  slugify,
  type ApplicationDetail,
  type ApplicationNote,
  type ApplicationSummary,
  type Posting,
  type PostingInput,
} from "../domain";
import {
  validateManualCandidate,
  type ManualCandidateInput,
} from "../manualCandidate";
import { normalizePhone } from "../../domain/phone";
import { RecruitingApiError, type RecruitingClient } from "./types";

type StoredApplication = Omit<
  ApplicationDetail,
  "notes" | "posting_slug" | "ai_review"
>;
export type LocalRecruitingSeed = {
  postings?: Partial<Posting>[];
  applications?: Partial<StoredApplication>[];
  notes?: (ApplicationNote & { application_id: number })[];
  /** GAVION ai-hiring-review: the AI review per application (SQL: job_application_reviews). */
  reviews?: (Partial<AiReview> & { application_id: number })[];
};
type Opts = {
  isAdmin: () => Promise<boolean>;
  now?: () => Date;
  author?: { id: number; name: string };
};

const nullIfBlank = (v: string | undefined | null) => {
  const t = (v ?? "").trim();
  return t ? t : null;
};
const fail = (code: string, detail?: string): never => {
  throw new RecruitingApiError(code, detail);
};

export function createLocalRecruitingClient(
  seed: LocalRecruitingSeed,
  opts: Opts,
): RecruitingClient & {
  state: { postings: Posting[]; applications: StoredApplication[] };
} {
  const now = opts.now ?? (() => new Date());
  let seq = 1000;
  const postings: Posting[] = (seed.postings ?? []).map((p, i) => ({
    id: i + 1,
    uid: `00000000-0000-4000-8000-${String(i + 1).padStart(12, "0")}`,
    slug: `poste-${i + 1}`,
    status: "draft",
    title_fr: "Poste",
    title_en: null,
    employment_type: "full_time",
    location: null,
    remote: "onsite",
    description_fr: "Description",
    description_en: null,
    requirements_fr: null,
    requirements_en: null,
    pay_text_fr: null,
    pay_text_en: null,
    published_at: null,
    closed_at: null,
    created_at: now().toISOString(),
    updated_at: now().toISOString(),
    applications: 0,
    new_applications: 0,
    ...p,
  }));
  const applications: StoredApplication[] = (seed.applications ?? []).map(
    (a, i) => {
      const created = a.created_at ?? now().toISOString();
      return {
        id: i + 1,
        uid: `00000000-0000-4000-9000-${String(i + 1).padStart(12, "0")}`,
        posting_id: null,
        posting_title: "Candidature spontanée",
        name: "Candidat",
        email: "candidat@exemple.test",
        phone: "514 555-0100",
        phone_e164: "+15145550100",
        language: "fr",
        availability: "Lundi matin",
        status: "new",
        has_cv: false,
        cv_filename: null,
        cv_size: null,
        cv_path: null,
        letter_filename: null,
        letter_size: null,
        letter_path: null,
        has_letter: false,
        message: null,
        source: "website",
        profile_url: null,
        consent_at: created,
        created_at: created,
        purge_after:
          a.status === "hired"
            ? null
            : initialPurgeAfter(new Date(created)).toISOString(),
        ...a,
      };
    },
  );
  const notes = [...(seed.notes ?? [])];
  const reviews = new Map<number, AiReview>(
    (seed.reviews ?? []).map(({ application_id, ...r }) => [
      application_id,
      { ...EMPTY_AI_REVIEW, ...r },
    ]),
  );
  const admin = async () => {
    if (!(await opts.isAdmin())) fail("forbidden");
  };
  const withCounts = (p: Posting): Posting => ({
    ...p,
    applications: applications.filter((a) => a.posting_id === p.id).length,
    new_applications: applications.filter(
      (a) => a.posting_id === p.id && a.status === "new",
    ).length,
  });
  const summary = (a: StoredApplication): ApplicationSummary => {
    const p = postings.find((x) => x.id === a.posting_id);
    return {
      id: a.id,
      uid: a.uid,
      posting_id: a.posting_id,
      posting_title: p?.title_fr ?? a.posting_title,
      posting_slug: p?.slug ?? null,
      name: a.name,
      email: a.email,
      phone: a.phone,
      phone_e164: a.phone_e164,
      language: a.language,
      availability: a.availability,
      source: a.source,
      profile_url: a.profile_url,
      status: a.status,
      has_cv: a.cv_path !== null,
      cv_filename: a.cv_filename,
      cv_size: a.cv_size,
      has_letter: a.letter_path !== null,
      letter_filename: a.letter_filename,
      letter_size: a.letter_size,
      consent_at: a.consent_at,
      created_at: a.created_at,
      purge_after: a.purge_after,
      ai_review: reviewSummary(reviews.get(a.id) ?? null),
    };
  };
  const detail = (a: StoredApplication): ApplicationDetail => ({
    ...summary(a),
    message: a.message,
    cv_path: a.cv_path,
    letter_path: a.letter_path,
    notes: notes
      .filter((n) => n.application_id === a.id)
      .map(({ application_id: _a, ...n }) => n),
    ai_review: reviews.get(a.id) ?? null,
  });
  const findApp = (id: number) =>
    applications.find((a) => a.id === id) ?? fail("not_found");
  const findPosting = (id: number) =>
    postings.find((p) => p.id === id) ?? fail("not_found");
  const validate = (id: number | null, input: PostingInput) => {
    if (!input.title_fr.trim() || input.title_fr.trim().length > 200)
      fail("invalid_posting", "title_fr");
    if (!input.description_fr.trim()) fail("invalid_posting", "description_fr");
    if (!EMPLOYMENT_TYPES.includes(input.employment_type))
      fail("invalid_posting", "employment_type");
    if (!REMOTE_MODES.includes(input.remote)) fail("invalid_posting", "remote");
    const slug = input.slug.trim().toLowerCase();
    if (slug) {
      if (slug.length > SLUG_MAX || !SLUG_PATTERN.test(slug))
        fail("invalid_posting", "slug");
      // review M6: a published posting keeps its slug (SQL twin: recruiting_save_posting)
      const current = id === null ? null : findPosting(id);
      if (current?.published_at && slug !== current.slug) fail("slug_locked");
      if (postings.some((p) => p.slug === slug && p.id !== id))
        fail("slug_taken");
    }
    return slug || null;
  };
  const fields = (input: PostingInput) => ({
    title_fr: input.title_fr.trim(),
    title_en: nullIfBlank(input.title_en),
    employment_type: input.employment_type,
    location: nullIfBlank(input.location),
    remote: input.remote,
    description_fr: input.description_fr.trim(),
    description_en: nullIfBlank(input.description_en),
    requirements_fr: nullIfBlank(input.requirements_fr),
    requirements_en: nullIfBlank(input.requirements_en),
    pay_text_fr: nullIfBlank(input.pay_text_fr),
    pay_text_en: nullIfBlank(input.pay_text_en),
  });

  return {
    state: { postings, applications },
    async postings() {
      await admin();
      const rank = (p: Posting) => (p.status === "closed" ? 1 : 0);
      return postings
        .map(withCounts)
        .sort(
          (a, b) =>
            rank(a) - rank(b) ||
            (b.published_at ?? b.created_at).localeCompare(
              a.published_at ?? a.created_at,
            ) ||
            b.id - a.id,
        );
    },
    async savePosting(id, input) {
      await admin();
      const slug = validate(id, input);
      const at = now().toISOString();
      if (id === null) {
        const p: Posting = {
          id: ++seq,
          uid: crypto.randomUUID(),
          slug:
            slug ??
            freeSlug(
              slugify(input.title_fr),
              new Set(postings.map((x) => x.slug)),
            ),
          status: "draft",
          ...fields(input),
          published_at: null,
          closed_at: null,
          created_at: at,
          updated_at: at,
          applications: 0,
          new_applications: 0,
        };
        postings.push(p);
        return withCounts(p);
      }
      const p = findPosting(id);
      Object.assign(p, fields(input), { slug: slug ?? p.slug, updated_at: at });
      return withCounts(p);
    },
    async setPostingStatus(id, status) {
      await admin();
      const p = findPosting(id);
      if (p.status === status) return withCounts(p);
      if (status === "closed" && p.status !== "published")
        fail("invalid_transition");
      const at = now().toISOString();
      if (status === "published") {
        p.published_at = p.published_at ?? at;
        p.closed_at = null;
      } else p.closed_at = at;
      p.status = status;
      p.updated_at = at;
      return withCounts(p);
    },
    async deletePosting(id) {
      await admin();
      const p = findPosting(id);
      if (p.status !== "draft" || applications.some((a) => a.posting_id === id))
        fail("posting_not_deletable");
      postings.splice(postings.indexOf(p), 1);
    },
    async applications(f) {
      await admin();
      return applications
        .filter(
          (a) =>
            (f.postingId == null || a.posting_id === f.postingId) &&
            (!f.spontaneous || a.posting_id === null) &&
            (!f.status || a.status === f.status),
        )
        .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id)
        .map(summary);
    },
    async application(id) {
      await admin();
      return detail(findApp(id));
    },
    async setApplicationStatus(id, status) {
      await admin();
      if (!APPLICATION_STATUSES.includes(status)) fail("invalid_status");
      const a = findApp(id);
      const next = purgeAfterFor(
        new Date(a.created_at),
        a.status,
        status,
        a.purge_after ? new Date(a.purge_after) : null,
        now(),
      );
      a.purge_after = next ? next.toISOString() : null;
      a.status = status;
      return detail(a);
    },
    async addNote(id, text) {
      await admin();
      const t = text.trim();
      if (!t || t.length > 5000) fail("invalid_note");
      findApp(id);
      const n = {
        id: ++seq,
        application_id: id,
        text: t,
        created_at: now().toISOString(),
        sales_id: opts.author?.id ?? null,
        author: opts.author?.name ?? null,
      };
      notes.push(n);
      const { application_id: _a, ...note } = n;
      return note;
    },
    async deleteApplication(id) {
      await admin();
      const a = findApp(id);
      applications.splice(applications.indexOf(a), 1);
      reviews.delete(id); // cascade (92_)
      for (let i = notes.length - 1; i >= 0; i--)
        if (notes[i].application_id === id) notes.splice(i, 1);
    },
    async eraseByEmail(email, reason) {
      await admin();
      if (!["person_request", "cai_order", "other"].includes(reason))
        fail("invalid_reason");
      const e = email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+$/.test(e)) fail("invalid_email");
      const hits = applications.filter((a) => a.email?.toLowerCase() === e);
      let files = 0;
      for (const a of hits) {
        files += (a.cv_path ? 1 : 0) + (a.letter_path ? 1 : 0);
        applications.splice(applications.indexOf(a), 1);
        reviews.delete(a.id);
        for (let i = notes.length - 1; i >= 0; i--)
          if (notes[i].application_id === a.id) notes.splice(i, 1);
      }
      return { count: hits.length, purgeQueued: files };
    },
    async newCount() {
      await admin();
      return applications.filter((a) => a.status === "new").length;
    },
    async cvUrl(path) {
      await admin();
      return `https://demo.invalid/job-cvs/${encodeURIComponent(path)}`;
    },
    async addApplication(input: ManualCandidateInput) {
      await admin();
      // the RPC's rules (95_), field by field
      const errors = validateManualCandidate({
        name: input.name,
        posting:
          input.posting_id === null ? "spontaneous" : String(input.posting_id),
        source: input.source,
        email: input.email ?? "",
        phone: input.phone ?? "",
        availability: input.availability ?? "",
        profile_url: input.profile_url ?? "",
        message: input.message ?? "",
      });
      const first = (Object.keys(errors) as (keyof typeof errors)[])[0];
      if (first) fail("invalid_application", first);
      const posting =
        input.posting_id === null
          ? null
          : (postings.find((p) => p.id === input.posting_id) ??
            fail("invalid_application", "posting_id"));
      const uid = crypto.randomUUID();
      const at = now().toISOString();
      const file = (f: ManualCandidateInput["cv"], n: string) =>
        f
          ? {
              path: `${uid}/${n}${seq}.${f.ext}`,
              name: f.filename,
              size: f.file.size,
            }
          : null;
      const cv = file(input.cv, "cvDEMO");
      const letter = file(input.letter, "ltDEMO");
      const a: StoredApplication = {
        id: ++seq,
        uid,
        posting_id: posting?.id ?? null,
        posting_title: posting?.title_fr ?? "Candidature spontanée",
        name: input.name.trim(),
        email: input.email?.trim().toLowerCase() || null,
        phone: input.phone?.trim() || null,
        phone_e164: input.phone ? normalizePhone(input.phone) : null,
        language: "fr",
        availability: input.availability?.trim() || null,
        status: "new",
        has_cv: cv !== null,
        cv_filename: cv?.name ?? null,
        cv_size: cv?.size ?? null,
        cv_path: cv?.path ?? null,
        has_letter: letter !== null,
        letter_filename: letter?.name ?? null,
        letter_size: letter?.size ?? null,
        letter_path: letter?.path ?? null,
        message: input.message?.trim() || null,
        source: input.source,
        profile_url: input.profile_url?.trim() || null,
        consent_at: at,
        created_at: at,
        purge_after: initialPurgeAfter(new Date(at)).toISOString(),
      };
      applications.push(a);
      reviews.set(a.id, { ...EMPTY_AI_REVIEW }); // 92_'s insert trigger: a pending review
      return detail(a);
    },
    async requestReview(id) {
      await admin();
      findApp(id);
      const cur = reviews.get(id);
      if (
        cur?.requested_at &&
        now().getTime() - Date.parse(cur.requested_at) <
          REVIEW_REQUEST_COOLDOWN_MS
      )
        fail("too_soon");
      const r: AiReview = {
        ...EMPTY_AI_REVIEW,
        requested_at: now().toISOString(),
      };
      reviews.set(id, r);
      return r;
    },
  };
}
